import {
  Address,
  CampaignDetail,
  CartCountResponse,
  ErrorResponse,
  RedeemResponse,
  ServiceResponse,
  Style,
  UseCampaignResponse,
} from '@bzbs/react-api-client';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { campaignInterfaceType, campaignPointType, campaignType } from '../constant';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';
import { useAppLocaleContext } from './locale_provider';
import { useUserContext } from './user_provider';
import { useAnalyticsContext } from './analytics_provider';

export type CampaignButtonType =
  | 'redeem'
  | 'get_points'
  | 'add_to_cart'
  | 'survey'
  | 'website'
  | 'draw'
  | 'donate'
  | 'delivery';

export type CampaignDetailButtonState = {
  enabled: boolean;
  type: CampaignButtonType;
  display: boolean;
};

export type CampaignState =
  | 'redeemable'
  | 'point_not_enough'
  | 'expired'
  | 'cool_down'
  | 'fail_in_condition'
  | 'sold_out'
  | 'redeemed';

export type AfterRedeemDestination = 'delivery' | 'draw' | 'code' | 'get_points';

export type AfterRedeem = {
  destination: AfterRedeemDestination;
  redeemData: RedeemResponse;
  clear: () => void;
};

export type AfterUse = {
  useData: UseCampaignResponse;
  clear: () => void;
};

export type CampaignDetailContextType = {
  data: CampaignDetail | null;
  style: Style | null;
  subStyle: Style | null;
  quantity: number;
  totalPrice: number;
  deliveryAddress: Address | null;
  afterRedeem: AfterRedeem | null;
  afterUse: AfterUse | null;
  buttonState: CampaignDetailButtonState;
  campaignState: CampaignState;
  interfaceWebsite: string | null;
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    setError: (error: ErrorResponse) => void;
    fetchData: (id: string) => Promise<ServiceResponse<CampaignDetail>>;
    redeem: (
      addressOverride?: Address,
      options?: { [key: string]: unknown }
    ) => Promise<ServiceResponse<RedeemResponse>>;
    use: () => Promise<ServiceResponse<UseCampaignResponse>>;
    addToFavorite(): Promise<ServiceResponse<unknown>>;
    removeFromFavorite(): Promise<ServiceResponse<unknown>>;
    setStyle: (style: Style) => void;
    setSubStyle: (style: Style) => void;
    setQuantity: (quantity: number) => void;
    setDeliveryAddress: (address: Address) => void;
    addToCart: () => Promise<ServiceResponse<CartCountResponse>>;
    clear: () => void;
  };
};

export const CampaignDetailContext = createContext({
  data: null,
  style: null,
  subStyle: null,
  quantity: 1,
  totalPrice: 0,
  deliveryAddress: null,
  afterRedeem: null,
  afterUse: null,
  buttonState: { enabled: false, type: 'redeem', display: false },
  campaignState: 'redeemable',
  interfaceWebsite: null,
  isLoading: false,
  error: null,
  actions: {
    setError: () => {},
    fetchData: async () => ({ type: 'client-error', message: 'Not implemented' }),
    redeem: async () => ({ type: 'client-error', message: 'Not implemented' }),
    use: async () => ({ type: 'client-error', message: 'Not implemented' }),
    addToFavorite: async () => ({ type: 'client-error', message: 'Not implemented' }),
    removeFromFavorite: async () => ({ type: 'client-error', message: 'Not implemented' }),
    setStyle: () => {},
    setSubStyle: () => {},
    setQuantity: () => {},
    setDeliveryAddress: () => {},
    addToCart: async () => ({ type: 'client-error', message: 'Not implemented' }),
    clear: () => {},
  },
} as CampaignDetailContextType);

export function useCampaignDetailContext() {
  return useContext(CampaignDetailContext);
}

export function CampaignDetailProvider({ children }: { children: React.ReactNode }) {
  const campaignId = useRef<string | undefined>(undefined);
  const [data, setData] = useState<CampaignDetail | null>(null);
  const [afterRedeem, setAfterRedeem] = useState<AfterRedeem | null>(null);
  const [afterUse, setAfterUse] = useState<AfterUse | null>(null);
  const [campaignState, setCampaignState] = useState<CampaignState>('redeemable');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { localeId } = useAppLocaleContext();
  const { actions: analyticsActions } = useAnalyticsContext();

  const [style, setStyle] = useState<Style | null>(null);
  const [subStyle, setSubStyle] = useState<Style | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);
  const [deliveryAddress, setDeliveryAddress] = useState<Address | null>(null);

  const interfaceWebsite = useRef<string | null>(null);

  const [buttonState, setButtonState] = useState<CampaignDetailButtonState>({
    enabled: false,
    type: 'redeem',
    display: false,
  });

  const { bzbsService } = useBuzzebeesServiceContext();
  const user = useUserContext();
  const userData = user.user;
  const userPoints = user.points;

  useEffect(() => {
    if (data && userData) {
      updateButtonState();
    }
  }, [data, userData, userPoints, style, subStyle, quantity]);

  useEffect(() => {
    if (data && userData) {
      checkCondition();
    }
  }, [data, userData, userPoints]);

  useEffect(() => {
    if (data && style && subStyle) {
      calculatePrice();
    } else if (data) {
      if (style === null) {
        setSubStyle(null);
      }
    } else {
      setTotalPrice(0);
      setStyle(null);
      setSubStyle(null);
    }
  }, [data, style, subStyle, quantity]);

  const fetchData = async (id: string): Promise<ServiceResponse<CampaignDetail>> => {
    campaignId.current = id;

    setIsLoading(true);

    await user.actions.fetchUser();
    await user.actions.fetchPoint();
    await user.actions.fetchCartCount();

    const response = await bzbsService.campaignApi.campaignDetails({ id, deviceLocale: localeId });
    setIsLoading(false);

    if (response.type === 'success') {
      setData(response.model);
      analyticsActions.trackEvent({
        action: 'on_click',
        name: 'view_campaign',
        category: 'campaign_list',
      });
    } else {
      setError(response);
    }

    return response;
  };

  const updateButtonState = () => {
    if (!data) {
      return;
    }

    const type = data.Type ?? 0;
    const pointType = data.PointType ?? campaignPointType.use;

    const pointPerUnit = data.PointPerUnit ?? 0;
    let pointEnough = true;

    if (pointType === campaignPointType.use && pointPerUnit > userPoints) {
      pointEnough = false;
    }

    let buttonType: CampaignButtonType = 'redeem';
    let display = true;
    let canAddToCart = true;

    const nonRedeemable = [campaignType.event, campaignType.media, campaignType.news];

    switch (type) {
      case campaignType.buy:
        buttonType = 'add_to_cart';

        if (data.SubCampaignStyles?.hasStyle) {
          if (!style || (style.subitems?.length && !subStyle)) {
            canAddToCart = false;
          }
        }
        break;
      case campaignType.interface:
        switch (data.InterfaceDisplay) {
          case campaignInterfaceType.survey:
          case campaignInterfaceType.surveyApprove:
            buttonType = 'survey';
            break;
          default:
            buttonType = 'website';
            break;
        }
        interfaceWebsite.current = data.Website ?? null;
        break;
      case campaignType.draw:
        if (data.Delivered) {
          buttonType = 'delivery';
        } else {
          buttonType = 'draw';
        }
        break;
      case campaignType.donate:
        if (data.Delivered) {
          buttonType = 'delivery';
        } else {
          buttonType = 'donate';
        }
        break;
      default:
        buttonType = pointType === campaignPointType.use ? 'redeem' : 'get_points';
        if (data.Delivered) {
          buttonType = 'delivery';
        }
        display = !nonRedeemable.includes(type);
        break;
    }

    setButtonState({
      enabled: pointEnough && canAddToCart,
      type: buttonType,
      display,
    });
  };

  const checkCondition = () => {
    if (!data) {
      return;
    }

    let state = 'redeemable';

    // Use local time for comparison by adjusting for timezone offset
    const today = new Date();
    const timezoneOffsetMinutes = today.getTimezoneOffset();
    const currentTimeLocal = Math.floor((Date.now() - timezoneOffsetMinutes * 60 * 1000) / 1000);

    if (
      data.Type !== campaignType.buy &&
      data.PointType === campaignPointType.use &&
      data.PointPerUnit &&
      userPoints < data.PointPerUnit
    ) {
      state = 'point_not_enough';
    } else if (data.ItemCountSold && data.Quantity && data.ItemCountSold >= data.Quantity) {
      state = 'sold_out';
    } else if (data.RedeemMostPerPerson && data.RedeemCount && data.RedeemCount >= data.RedeemMostPerPerson) {
      state = 'redeemed';
    } else if (data.ConditionAlertID === 2) {
      state = 'redeemed';
    } else if (data.NextRedeemDate && data.NextRedeemDate > currentTimeLocal) {
      state = 'cool_down';
    } else if (data.ExpireDate && data.ExpireDate < currentTimeLocal) {
      state = 'expired';
    } else if (data.IsConditionPass === false) {
      state = 'fail_in_condition';
    }

    setCampaignState(state as CampaignState);
  };

  const redeem = async (
    addressOverride?: Address,
    options?: { [key: string]: unknown }
  ): Promise<ServiceResponse<RedeemResponse>> => {
    if (!campaignId.current) {
      return Promise.reject('No campaign id');
    }

    const id = subStyle?.campaignId ?? style?.campaignId ?? campaignId.current;
    const addressToUse = addressOverride ?? deliveryAddress;

    setIsLoading(true);
    const response = await bzbsService.campaignApi.redeem({ id: id, addressKey: addressToUse?.RowKey, options });
    setIsLoading(false);

    if (response.type === 'success') {
      handleAfterRedeem(response.model);
      setError(null);

      analyticsActions.trackEvent({
        action: 'on_click',
        name: 'redeem_success',
        category: 'campaign_redeem',
      });

      fetchData(campaignId.current!);
    } else {
      setError(response);
    }

    return response;
  };

  const handleAfterRedeem = (redeemData: RedeemResponse) => {
    let dest: AfterRedeemDestination = 'code';

    if (redeemData.PointType === campaignPointType.use) {
      if (data?.Delivered) {
        dest = 'delivery';
      } else if (data?.Type === campaignType.draw) {
        dest = 'draw';
      }
    } else if (redeemData.PointType === campaignPointType.get) {
      dest = 'get_points';
    }

    setAfterRedeem({
      destination: dest,
      redeemData,
      clear: () => {
        setAfterRedeem(null);
      },
    });
  };

  const use = async (): Promise<ServiceResponse<UseCampaignResponse>> => {
    if (!afterRedeem?.redeemData || !afterRedeem.redeemData.RedeemKey) {
      return Promise.reject('No redeem data');
    }

    setIsLoading(true);
    const response = await bzbsService.historyApi.use({ redeemKey: afterRedeem.redeemData.RedeemKey });
    setIsLoading(false);

    if (response.type === 'success') {
      handleAfterUse(response.model);
      setError(null);
      fetchData(campaignId.current!);
    } else {
      setError(response);
    }

    return response;
  };

  const handleAfterUse = (useData: UseCampaignResponse) => {
    setAfterUse({
      useData,
      clear: () => {
        setAfterRedeem(null);
        setAfterUse(null);
      },
    });
  };

  const addToFavorite = async (): Promise<ServiceResponse<unknown>> => {
    if (!campaignId.current) {
      return Promise.reject('No campaign id');
    }

    setIsLoading(true);
    const response = await bzbsService.campaignApi.addToFavorite(campaignId.current);
    setIsLoading(false);
    if (response.type === 'success') {
      fetchData(campaignId.current);
    }
    return response;
  };

  const removeFromFavorite = async (): Promise<ServiceResponse<unknown>> => {
    if (!campaignId.current) {
      return Promise.reject('No campaign id');
    }

    setIsLoading(true);
    const response = await bzbsService.campaignApi.removeFromFavorite({ id: campaignId.current });
    setIsLoading(false);
    if (response.type === 'success') {
      fetchData(campaignId.current);
    }
    return response;
  };

  const addToCart = async (): Promise<ServiceResponse<CartCountResponse>> => {
    if (!data) {
      return Promise.reject('No data');
    }
    const type = data.Type ?? 0;
    if (type !== campaignType.buy) {
      return Promise.reject('This campaign is not add to cart type');
    }

    const id = subStyle?.campaignId ?? style?.campaignId ?? data.ID?.toString();

    if (!id) {
      return Promise.reject('No campaign id');
    }

    const response = await bzbsService.cartApi.addCart({ id: id, mode: 'add', qty: quantity });
    if (response.type === 'success') {
      fetchData(campaignId.current!);
    } else {
      setError(response);
    }

    return response;
  };

  const calculatePrice = () => {
    setTotalPrice((subStyle?.price ?? style?.price ?? data?.PricePerUnit ?? 0) * quantity);
  };

  const clear = () => {
    setData(null);
    setStyle(null);
    setSubStyle(null);
    setQuantity(1);
    setTotalPrice(0);
    setDeliveryAddress(null);
    setAfterRedeem(null);
    setAfterUse(null);
    setButtonState({ enabled: false, type: 'redeem', display: false });
    setCampaignState('redeemable');
    setError(null);
  };

  return (
    <CampaignDetailContext.Provider
      value={{
        data,
        style,
        subStyle,
        quantity,
        totalPrice,
        deliveryAddress,
        afterRedeem,
        afterUse,
        buttonState,
        campaignState,
        interfaceWebsite: interfaceWebsite.current,
        actions: {
          setError,
          fetchData,
          redeem,
          use,
          addToFavorite,
          removeFromFavorite,
          setStyle,
          setSubStyle,
          setQuantity,
          setDeliveryAddress,
          addToCart,
          clear,
        },
        isLoading,
        error,
      }}
    >
      {children}
    </CampaignDetailContext.Provider>
  );
}
